<template>
  <div class="review-result-page">
    <!-- 页面头部：返回 / 标题 / 保存指示器 / 保存 / 下载文件 -->
    <header class="workbench-top">
      <!-- <a-button
        class="wb-back"
        @click="goBack"
        :disabled="crStatus !== 'completed'"
      >
        <ArrowLeftOutlined />
      </a-button> -->
      <img
        src="@/assets/icons/i-return.png"
        style="width: 36px; height: 36px; cursor: pointer"
        @click="goBack"
      />
      <div class="wb-title">
        <span class="wb-name">{{ crContractName }}审查结果</span>
        <span v-if="isDraft" class="draft-tag">草稿合同</span>
        <span v-if="autoSaveText && !isPdf" class="saved">
          <span
            class="save-dot"
            :class="`save-dot-${autoSaveStatus.state}`"
          ></span>
          {{ autoSaveText }}
        </span>
      </div>
      <div class="wb-actions">
        <a-button
          v-if="!isPdf"
          size="middle"
          :disabled="crStatus !== 'completed'"
          @click="onManualSave"
        >
          <template #icon><CheckOutlined /></template>
          保存
        </a-button>
        <a-button
          size="middle"
          type="primary"
          :disabled="crStatus !== 'completed'"
          @click="onDownloadFile"
        >
          <template #icon><DownloadOutlined /></template>
          下载文件
        </a-button>
      </div>
    </header>

    <!-- 三列主体 -->
    <div class="main-grid">
      <!-- 左侧：任务过程（可整体收起，由 taskPanelCollapsed 驱动） -->
      <TaskPanel
        :collapsed="taskPanelCollapsed"
        :status="crStatus"
        :stages="stagesForPanel"
        :id="reviewId"
        :step-ms="taskStepMs"
        @collapse="taskPanelCollapsed = true"
      />

      <!-- 中间：编辑器 + 底栏 -->
      <div class="doc-column">
        <!-- 编辑器顶部条：仅在 TaskPanel 收起时出现，提供“展开AI审查助手”入口 -->
        <div v-show="taskPanelCollapsed" class="doc-tabs-bar">
          <button
            class="expand-assistant-btn"
            @click="taskPanelCollapsed = false"
          >
            <!-- <PanelLeftOutlined /> -->
            <SvgIcon name="pack-up" :size="15" />
            <span>展开AI审查助手</span>
          </button>
        </div>
        <PdfPanel
          v-if="isPdf"
          ref="pdfPanelRef"
          :contract-name="resultMeta.fileName || crContractName"
          :document-buffer="docBuffer || documentBuffer"
        />
        <DocPanel
          v-else
          ref="docPanelRef"
          :status="crStatus"
          :contract-name="resultMeta.fileName || crContractName"
          :reviewer="resultMeta.reviewerName || reviewer"
          :document-buffer="docBuffer || documentBuffer"
          :id="reviewId"
          @auto-save-status="onAutoSaveStatus"
        />
        <!-- 底部状态条（仅中间列） -->
        <div class="bottom-bar">
          <span>{{ bottomLeftText }}</span>
          <span class="bottom-bar-right">
            <span>{{ bottomRightText }}</span>
            <span
              v-if="autoSaveText && !isPdf"
              class="auto-save-indicator"
              :class="`auto-save-${autoSaveStatus.state}`"
              >{{ autoSaveText }}</span
            >
          </span>
        </div>
      </div>

      <!-- 右侧：审查意见 -->
      <OpinionPanel
        :status="crStatus"
        :opinion-list="opinionList"
        :applying-all="applyingAll"
        :canceling-all="cancelingAll"
        :readonly="isPdf"
        @locate="onLocateOpinion"
        @apply="onApplyOpinion"
        @cancel="onCancelOpinion"
        @apply-all="onApplyAll"
        @cancel-all="onCancelAll"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { CheckOutlined, DownloadOutlined } from "@ant-design/icons-vue";
import TaskPanel from "./components/TaskPanel.vue";
import DocPanel from "./components/DocPanel.vue";
import PdfPanel from "./components/PdfPanel.vue";
import OpinionPanel from "./components/OpinionPanel.vue";
import {
  crContractName,
  crStatus,
  crReviewId,
  crReviewResult,
} from "./crSharedState";
import {
  getOrStartReview,
  getFile,
  getGetReviewStages,
  getToggleClauseRevision,
} from "@/api/contractReview";

/* 文件名扩展名（小写，无扩展名返回空串） */
const getExtFromName = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

/* PDF 判定优先级：query.fileExtension > 审查返回 fileExtension > 文件名扩展名 > blob mime
 * - fileExtension 为后端统一字段（".pdf"/".docx"/".doc"）；query 由配置页带入，
 *   reviewing 态即可决定挂载哪个面板
 * - GetOrStartReview 返回的 fileExtension 为权威来源
 * - blob mime 可能是 octet-stream，仅最后兜底 */
const isPdf = computed(() => {
  const q = route.query;
  const fe = String(
    q.fileExtension || reviewData.value?.fileExtension || "",
  ).toLowerCase();
  if (fe) return fe.replace(/^\./, "") === "pdf";
  const name = q.contractName || resultMeta.value.fileName || "";
  const ext = getExtFromName(name);
  if (ext) return ext === "pdf";
  return docBuffer.value?.type === "application/pdf";
});

/* JSON 字段防御性解析：后端可能把 summary / clauses 当作字符串返回 */
const parseJsonField = (val) => {
  if (val == null) return null;
  if (typeof val === "string") {
    if (!val.trim()) return null;
    try {
      return JSON.parse(val);
    } catch (e) {
      console.warn("[ReviewResult] JSON.parse failed:", e);
      return null;
    }
  }
  return val;
};

/* 兼容 axios 包装：直接返回 res 或 res.data */
const reviewData = computed(() => {
  const res = crReviewResult.value;
  if (!res) return null;
  return (res && (res.data || res)) || null;
});

/* 顶部元信息 */
const resultMeta = computed(() => {
  const d = reviewData.value || {};
  return {
    fileName: d.fileName || d.contractName || crContractName.value || "",
    partyA: d.partyA || "",
    partyB: d.partyB || "",
    stance: d.stance || "",
    scale: d.scale || "",
    reviewerName: route.query.reviewer || d.reviewerName || d.reviewer || "",
    completeTime: d.completeTime || "",
  };
});

/* 草稿合同标记：优先 getOrStartReview 响应（历史查看时 query 无此参数），
 * 其次配置页 query 带入的字符串布尔；均无则不显示标签 */
const isDraft = computed(() => {
  const d = reviewData.value || {};
  if (d.isDraft !== undefined && d.isDraft !== null) {
    return d.isDraft === true || d.isDraft === "true";
  }
  return route.query.isDraft === "true";
});

/* 进入方式：route.query.status === 'Completed' 表示查看已审查过的历史记录，
 *   详情返回快 → TaskPanel 步进保持原速（1200ms）；
 *   无该参数表示发起 / 重审合同 → 后端实际审查较慢，步进放慢到 10s/节点，
 *   避免动画过早跑完。结束逻辑（crStatus 翻 completed 时全部标 done）不变。 */
const isHistoryReview = computed(() => route.query.status === "Completed");
const taskStepMs = computed(() => (isHistoryReview.value ? 1200 : 10000));

/* 风险等级归一化 */
const riskLevelMap = {
  high: "high",
  High: "high",
  HighRisk: "high",
  高: "high",
  medium: "medium",
  Medium: "medium",
  中: "medium",
  low: "low",
  Low: "low",
  LowRisk: "low",
  低: "low",
};
const normalizeRiskLevel = (lvl) => {
  if (!lvl) return "medium";
  return riskLevelMap[lvl] || "medium";
};
const riskLabelMap = { high: "高风险", medium: "中风险", low: "低风险" };

/* reviewType 枚举 → 中文（用于 meta 标签展示；原始值同时透传给 OpinionPanel 做筛选） */
const reviewTypeMap = {
  Substantive: "实质审查",
  Formal: "形式审查",
  Subject: "主体审查",
};
const reviewTypeLabel = (t) => reviewTypeMap[t] || t || "";

/* 右侧意见列表：JSON.parse clauses 后映射为 OpinionCard 所需格式 */
const opinionList = computed(() => {
  const d = reviewData.value || {};
  const clauses = parseJsonField(d.clauses);
  if (!Array.isArray(clauses)) return [];
  console.log(
    "[ReviewResult] 各条款 revisionStatus:",
    clauses.map((c, idx) => ({
      num: c.sortOrder ?? idx + 1,
      revisionStatus: c.revisionStatus,
      title: (c.title || "").slice(0, 12),
    })),
  );
  return clauses.map((c, idx) => {
    const riskLevel = normalizeRiskLevel(c.riskLevel || c.risk);
    const num = c.sortOrder ?? idx + 1;
    return {
      num,
      title: c.title || "未命名意见",
      riskLevel,
      riskLabel: riskLabelMap[riskLevel] || "",
      // 保留原始 reviewType 用于 tab 筛选
      reviewType: c.reviewType || "",
      // meta 展示中文标签
      meta: [reviewTypeLabel(c.reviewType), c.category].filter(Boolean),
      riskDesc: c.issue || c.riskDesc || "",
      suggestion: c.suggestion || "",
      // 修订版 / 清洁版 由 OpinionCard 按 version 切换
      // 后端字段映射：markedVersion → 修订版，cleanVersion → 清洁版，clauseContent → 原文
      // 前端字段与后端语义同名：revisedVersion / cleanVersion / originalContent
      revisedVersion: c.markedVersion || c.revisedVersion || c.revised || "",
      cleanVersion: c.cleanVersion || "",
      originalContent: c.clauseContent || c.originalContent || "",
      legalBasis: c.legalBasis || "",
      // 定位原文用：Word w14:paraId
      paraId: c.paraId || c.para_id || c.paragraphId || c.paragraph_id || null,
      // 应用修订时调 ToggleClauseRevision 用
      clauseId: c.clauseId || c.clause_id || c.id || null,
      // 按钮展示只跟随后端 revisionStatus：Original=未应用 / Applied=已应用。
      // 点击应用/取消时本地翻转 revisionStatus 让按钮立即响应，后端状态持久化由后续接口入参补全。
      applied: c.revisionStatus === "Applied",
    };
  });
});

/* 已应用意见的编辑器句柄：{ [num]: { revisionIds: number[], version } }。
 * 仅用于"取消修订"时在编辑器里 reject 对应 tracked change，不再参与按钮显示。 */
const appliedMap = ref({});

/* 本地翻转某条款的 revisionStatus（Optimistic UI）。
 * clauses 在 reviewData 里是 JSON 字符串，opinionList 每次重新 parse，
 * 所以必须改写回源字符串才能让 computed 重算、按钮刷新。 */
const setClauseRevisionStatus = (clauseId, status) => {
  if (clauseId == null) return;
  const d = reviewData.value;
  if (!d) return;
  const wasString = typeof d.clauses === "string";
  const list = parseJsonField(d.clauses);
  if (!Array.isArray(list)) return;
  const target = list.find(
    (c) => (c.clauseId || c.clause_id || c.id) === clauseId,
  );
  if (!target) return;
  target.revisionStatus = status;
  if (wasString) d.clauses = JSON.stringify(list);
};

/* TaskPanel 整体收起态：true 时左列收到 0 宽、淡出，
   “展开AI审查助手”按钮在 DocPanel 顶部条出现供回展。 */
const taskPanelCollapsed = ref(false);

/* 审查节点（左侧任务过程）：getGetReviewStages 返回的原始结果 */
const reviewStages = ref(null);

/* 把 reviewStages 拆成数组传给 TaskPanel。
 * http 拦截器已经返回 response.data（HTTP body），所以 reviewStages.value 是 body 本身。
 * 后端实际结构：{ data: { reviewStages: [...] } }，每项 { status, step, title }。
 * 同时保留对常见命名/嵌套的兜底，方便后端字段调整时不至于直接空。 */
const stagesForPanel = computed(() => {
  const r = reviewStages.value;
  if (!r) return [];
  if (Array.isArray(r)) return r;
  if (typeof r !== "object") return [];
  const directKeys = ["reviewStages", "stages", "list", "items"];
  for (const k of directKeys) {
    if (Array.isArray(r[k])) return r[k];
  }
  // 双层嵌套：r.data.reviewStages / r.result.stages ...
  for (const k of ["data", "result", "body"]) {
    if (r[k] && typeof r[k] === "object") {
      for (const kk of directKeys) {
        if (Array.isArray(r[k][kk])) return r[k][kk];
      }
    }
  }
  console.warn(
    "[ReviewResult] stagesForPanel: 未在响应里找到 stages 数组，原始响应：",
    r,
  );
  return [];
});

const router = useRouter();
const route = useRoute();

const props = defineProps({
  // 'reviewing' = 审查中 | 'completed' = 审查完成
  // 默认 reviewing：进入结果页即处于等待态，等 getOrStartReview 返回后切到 completed
  status: { type: String, default: "reviewing" },
  contractName: { type: String, default: "动产买卖合同" },
  reviewer: { type: String, default: "张律师" },
  documentBuffer: {
    type: [ArrayBuffer, Uint8Array, Blob, File],
    default: null,
  },
});

/* 当前登录用户名（localStorage.user.name）：
 * "应用修订"时批注作者优先用审阅人，缺失则回退到用户名 */
const currentUserName = (() => {
  try {
    const raw = localStorage.getItem("user");
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.name) return u.name;
    }
  } catch {
    /* ignore */
  }
  return "";
})();

/* 把外部传入的 props 同步到共享状态（kevinHome 头部直接消费，响应式） */
watch(
  () => props.contractName,
  (v) => (crContractName.value = v),
  { immediate: true },
);
watch(
  () => props.status,
  (v) => (crStatus.value = v),
  { immediate: true },
);

/**
 * 发起审查：进入结果页时从 route.query 读取审查参数，立即调 getOrStartReview
 * - 接口同步返回最终审查结果，期间三栏显示骨架屏（status === 'reviewing'）
 * - 成功后写入 crReviewResult / crReviewId，并切到 'completed'
 * - 失败：http 拦截器已弹错，这里把用户引导回配置页重试
 *
 * query 形态：
 *   - 新审查（来自配置页）：contractId + stance + scale（+ 可选 fileId/fileExtension/customRequirement/reviewer/contractName/partyName/referenceFileIds）
 *   - 历史查看（来自最近审查 ?id=xxx）：仅 id；走 getOrStartReview 的 Get 分支，由后端返回既有审查
 */
const kickOffReview = async () => {
  const q = route.query;
  const hasNewReviewParams = !!(q.contractId && q.stance && q.scale);
  const hasHistoryId = !!q.id;
  if (!hasNewReviewParams && !hasHistoryId) {
    // 既无新审查参数也无历史 id → 回配置页
    if (!crReviewResult.value) {
      router.replace("/home/contract-review/requirements");
    }
    return;
  }
  // 新审查用 contractId；历史查看用 id。统一走 getOrStartReview，Get/Start 由后端按 id 状态判定
  const id = hasNewReviewParams ? q.contractId : q.id;
  const params = {
    id,
    fileId: q.fileId || null,
    contractName: q.contractName || "",
  };
  // 重置上一轮残留
  crReviewResult.value = null;
  crReviewId.value = null;
  docBuffer.value = null;
  reviewStages.value = null;
  autoSaveStatus.value = { state: "idle" };
  fileId.value = params.fileId;
  reviewId.value = params.id;
  crStatus.value = "reviewing";
  if (params.contractName) crContractName.value = params.contractName;

  // 文件二进制（历史查看无 fileId，等接口返回后再补拉）；getFile 失败不阻塞审查展示
  let filePromise = Promise.resolve();
  if (params.fileId) {
    filePromise = getFile(params.fileId)
      .then((blob) => {
        docBuffer.value = blob;
      })
      .catch((err) => {
        console.error("[ReviewResult] getFile error:", err);
      });
  }

  // 审查节点（左侧任务过程）：和主审查并行拉取，失败不阻塞主流程
  getGetReviewStages(params.id)
    .then((res) => {
      reviewStages.value = res;
      console.log("[ReviewResult] getGetReviewStages:", res.data.reviewStages);
    })
    .catch((err) => {
      console.error("[ReviewResult] getGetReviewStages error:", err);
    });

  try {
    // referenceFileIds（配置页 query 带入，逗号分隔）与 partyName（选中的具体主体）
    // 透传给审查接口；partyName 用于多甲方/多乙方时区分同 stance 的不同公司
    // isDraft：合同状态（true=草稿/false=正式），query 里是字符串需转布尔；
    // 历史查看无此参数 → 不传，由后端用已保存的值
    const res = await getOrStartReview({
      id: params.id,
      ...(q.referenceFileIds ? { referenceFileIds: q.referenceFileIds } : {}),
      ...(q.partyName ? { partyName: q.partyName } : {}),
      ...(q.isDraft !== undefined ? { isDraft: q.isDraft === "true" } : {}),
    });
    // 等文件二进制也到位再切到 completed：编辑器虽已先行渲染（不等审查接口），
    // 但 completed 后意见卡即可点"应用修订"，需保证那时编辑器已就绪
    await filePromise;
    crReviewResult.value = res;
    const data = (res && (res.data || res)) || {};
    crReviewId.value =
      data.id || data.reviewId || data.review_id || data.recordId || null;

    // 历史查看：URL 没有 fileId / contractName，从响应里补
    if (!params.fileId) {
      const historyFileId = data.fileId || data.file_id || data.uploadFileId;
      if (historyFileId) {
        fileId.value = historyFileId;
        getFile(historyFileId)
          .then((blob) => {
            docBuffer.value = blob;
          })
          .catch((err) => {
            console.error("[ReviewResult] getFile(history) error:", err);
          });
      }
    }
    if (!params.contractName) {
      const name = data.contractName || data.name || data.fileName;
      if (name) crContractName.value = name;
    }

    crStatus.value = "completed";
    console.log(
      "[ReviewResult] getOrStartReview success, reviewId=",
      crReviewId.value,
    );
  } catch (err) {
    console.error("[ReviewResult] getOrStartReview error:", err);
    router.replace("/home/contract-review/requirements");
  }
};

onMounted(kickOffReview);

/* 编辑器面板 ref */
const docPanelRef = ref(null);

/* PDF 面板 ref（isPdf 时替代 DocPanel） */
const pdfPanelRef = ref(null);

/* 从 GetFile 拉取的合同二进制，交给 DocPanel 渲染 */
const docBuffer = ref(null);

/* getFile 用的 fileId（上传接口返回），由 kickOffReview 从 route.query 提取并保留 */
const fileId = ref(null);

/* 审查/合同记录 ID（route.query 的 id 或 contractId），
   传给 DocPanel 作为 SaveRevisedFile 的 id 入参（不是 fileId） */
const reviewId = ref(null);

/* 自动保存状态：{ state: 'idle'|'saving'|'success'|'error', time?, error? } */
const autoSaveStatus = ref({ state: "idle" });
const onAutoSaveStatus = (status) => {
  autoSaveStatus.value = status;
};
const autoSaveText = computed(() => {
  const s = autoSaveStatus.value;
  if (!s || s.state === "idle") return "";
  if (s.state === "saving") return "自动保存中…";
  if (s.state === "success") return `${s.time}  更改已保存`;
  if (s.state === "error") return "保存失败";
  return "";
});

/* 意见面板：定位原文
 * 直接用后端返回的 paraId 调 scrollToParaId 滚动并高亮。
 */
const onLocateOpinion = (opinion) => {
  const paraId = opinion?.paraId;
  if (!paraId) {
    message.warning("该条款未携带段落定位信息");
    return;
  }
  if (!docPanelRef.value?.locateByParaId) {
    message.warning("编辑器未就绪");
    return;
  }
  const ok = docPanelRef.value.locateByParaId(paraId, {
    highlight: { color: "#ffe58f", durationMs: 2500 },
  });
  if (!ok) {
    message.warning("未在原文中定位到该条款");
  }
};

/* 意见面板：应用修订（单条内部实现）
 * 单条应用与全部应用共用。silent=true 时不弹单个 message，由调用方汇总结果。
 * @returns {Promise<boolean>} 是否成功
 */
const applyOne = async (opinion, version, content, { silent = false } = {}) => {
  if (!opinion?.num) return false;
  if (!docPanelRef.value?.applyRevision) {
    if (!silent) message.warning("编辑器未就绪");
    return false;
  }
  const paraId = opinion.paraId;
  if (!paraId) {
    if (!silent) message.warning("该条款未携带段落定位信息");
    return false;
  }
  const originalContent = opinion.originalContent;
  if (!originalContent || !originalContent.trim()) {
    if (!silent) message.warning("该条款未提供原文，无法应用修订");
    return false;
  }
  if (!content) {
    if (!silent) message.warning("当前版本无修改内容");
    return false;
  }

  try {
    const result = await docPanelRef.value.applyRevision({
      paraId,
      search: originalContent,
      replaceWith: content,
      author:
        resultMeta.value.reviewerName || currentUserName || props.reviewer,
    });
    if (!result?.revisionIds?.length) {
      if (!silent) message.warning("应用失败：未在原文中匹配到对应段落");
      return false;
    }
    appliedMap.value = {
      ...appliedMap.value,
      [opinion.num]: { revisionIds: result.revisionIds, version },
    };
    if (!silent) message.success("已应用修订");

    // 本地翻转 revisionStatus 让按钮立即变成"已应用"；同时通知后端 toggle
    setClauseRevisionStatus(opinion.clauseId, "Applied");
    if (opinion.clauseId != null) {
      getToggleClauseRevision(opinion.clauseId).catch((err) => {
        console.error("[ReviewResult] getToggleClauseRevision error:", err);
      });
    }
    return true;
  } catch (err) {
    console.error("[ReviewResult] applyRevision error:", err);
    if (!silent) message.error("应用修订失败");
    return false;
  }
};

/* 意见面板：应用修订（单条入口，由 OpinionCard 触发）
 * payload: { opinion, version: 'revised'|'clean', content: string }
 * content 统一是清洁版纯文本（OpinionCard.applyContent，cleanVersion），
 * 无论用户当前看的是修订版还是清洁版 tab。
 */
const onApplyOpinion = (payload) => {
  const { opinion, version, content } = payload || {};
  return applyOne(opinion, version, content);
};

/* 意见面板：应用全部修改
 * 顺序 await（ProseMirror 事务之间有 state 依赖，不能并行）。
 * 跳过已应用的项；按修订版（revised）应用。
 */
const applyingAll = ref(false);
const onApplyAll = async () => {
  if (applyingAll.value) return;
  // 跳过已应用的项，以及无可应用修订内容的项（修订版/清洁版均为空，
  // 与 OpinionCard 的 hasRevision 判定一致：该类意见卡"应用修订"按钮本就禁用）
  const pending = opinionList.value.filter(
    (o) => !o.applied && (o.revisedVersion || o.cleanVersion),
  );
  if (pending.length === 0) {
    message.info("没有可应用的修改");
    return;
  }
  applyingAll.value = true;
  let ok = 0;
  let fail = 0;
  try {
    for (const op of pending) {
      // 跳过缺关键字段的项，计入失败
      if (!op.paraId || !op.originalContent || !op.originalContent.trim()) {
        fail += 1;
        continue;
      }
      // 统一应用清洁版（cleanVersion 纯文本），与单条应用保持一致
      const content = op.cleanVersion || op.revisedVersion || "";
      const success = await applyOne(op, "clean", content, { silent: true });
      if (success) ok += 1;
      else fail += 1;
    }
    if (fail === 0) {
      message.success(`已应用修改（共 ${ok} 条）`);
    } else if (ok === 0) {
      message.error(`应用失败（共 ${fail} 条）`);
    } else {
      message.warning(`已应用 ${ok} 条，失败 ${fail} 条`);
    }
  } finally {
    applyingAll.value = false;
  }
};

/* 意见面板：取消修订
 * - 本次会话应用过（appliedMap 有 revisionIds）→ 在编辑器里 reject 掉
 * - 历史应用（保存文件后重新进入，appliedMap 已丢失）→ 按 paraId + 内容
 *   重新定位文档里的 tracked change 并 reject（DocPanel.cancelRevisionsInClause）
 * - 本地翻转 revisionStatus → "Original"，按钮立即变回"未应用"
 * - 通知后端 toggle 状态（与 applyOne 对称）
 * 注：后端目前不持久化"取消"状态，刷新后可能仍为 Applied，待后端补接口入参。 */
const cancelOne = (opinion, { silent = false } = {}) => {
  if (!opinion?.num) return false;
  const entry = appliedMap.value[opinion.num];

  // 1) 本次会话应用过的：在编辑器里 reject 对应 tracked change
  if (entry?.revisionIds?.length) {
    if (!docPanelRef.value?.cancelRevision) {
      if (!silent) message.warning("编辑器未就绪");
      return false;
    }
    try {
      const ok = docPanelRef.value.cancelRevision(entry.revisionIds);
      if (!ok) {
        if (!silent) message.error("取消修订失败");
        return false;
      }
    } catch (err) {
      console.error("[ReviewResult] cancelRevision error:", err);
      if (!silent) message.error("取消修订失败");
      return false;
    }
    const next = { ...appliedMap.value };
    delete next[opinion.num];
    appliedMap.value = next;
  } else if (opinion.paraId) {
    // 1b) 历史应用：appliedMap 无句柄，按条款在文档里重新定位并 reject。
    //     返回 0 = 段内本就没有该条款的修订（内容已干净），不算失败
    try {
      docPanelRef.value?.cancelRevisionsInClause?.({
        paraId: opinion.paraId,
        originalContent: opinion.originalContent,
        insertedTexts: [opinion.revisedVersion, opinion.cleanVersion].filter(
          Boolean,
        ),
      });
    } catch (err) {
      console.error("[ReviewResult] cancelRevisionsInClause error:", err);
    }
  }

  // 2) 本地翻转 revisionStatus → 按钮立即变回"未应用"
  setClauseRevisionStatus(opinion.clauseId, "Original");

  // 3) 通知后端 toggle（fire-and-forget）
  if (opinion.clauseId != null) {
    getToggleClauseRevision(opinion.clauseId).catch((err) => {
      console.error("[ReviewResult] getToggleClauseRevision error:", err);
    });
  }

  if (!silent) message.success("已取消修订");
  return true;
};

/* 单条取消入口（由 OpinionCard 触发） */
const onCancelOpinion = (opinion) => cancelOne(opinion);

/* 意见面板：取消全部应用
 * 目标 = 后端标记 Applied 的条款（含保存后重新进入的历史应用）∪ 本次会话
 * 应用的（appliedMap）。cancelOne 内部区分两条路径逐个 reject，同步 dispatch。
 */
const cancelingAll = ref(false);
const onCancelAll = () => {
  if (cancelingAll.value) return;
  const targets = opinionList.value.filter(
    (o) => o.applied || appliedMap.value[o.num],
  );
  if (targets.length === 0) {
    message.info("没有可取消的修订");
    return;
  }
  cancelingAll.value = true;
  try {
    let ok = 0;
    let fail = 0;
    for (const opinion of targets) {
      const success = cancelOne(opinion, { silent: true });
      if (success) ok += 1;
      else fail += 1;
    }
    if (fail === 0) {
      message.success(`已取消全部修订（共 ${ok} 条）`);
    } else if (ok === 0) {
      message.error(`取消失败（共 ${fail} 条）`);
    } else {
      message.warning(`已取消 ${ok} 条，失败 ${fail} 条`);
    }
  } finally {
    cancelingAll.value = false;
  }
};

/* 头部：返回合同审查入口 */
const goBack = () => {
  router.push("/home/contract-review");
};

/* 头部：手动保存（与自动保存同接口） */
const onManualSave = () => {
  docPanelRef.value?.manualSave?.();
};

/* 头部：下载文件（docx=修改后文件；PDF=原始文件，PDF 不可编辑） */
const onDownloadFile = () => {
  if (isPdf.value) {
    pdfPanelRef.value?.downloadFile?.();
  } else {
    docPanelRef.value?.downloadFile?.();
  }
};

/* 底栏文案 */
const bottomLeftText = computed(() =>
  crStatus.value === "completed" ? "合同正文已解析" : "合同正文解析中",
);
const bottomRightText = computed(() =>
  crStatus.value === "completed" ? "批注已完成" : "正在生成批注",
);
</script>

<style lang="less" scoped>
.review-result-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 112px);
  min-height: 500px;
  background: #f7f9fd;
  margin: 24px 0px 0px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #edf1f7;
}

/* 三列主体 */
.main-grid {
  flex: 1;
  display: flex;
  min-height: 0;
  background: #f3f6fb;
  border-top: 1px solid #edf1f7;
  // border-radius: 12px;
  overflow: hidden;
}

/* 中间列：编辑器 + 底栏 */
.doc-column {
  flex: 1 1 300px;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 底部状态条（位于中间列底部） */
.bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 18px;
  background: white;
  border-top: 1px solid #e7ebf2;
  font-size: 12px;
  color: #77849a;
  flex-shrink: 0;
}
.bottom-bar-right {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}
.auto-save-indicator {
  padding-left: 12px;
  border-left: 1px solid #e7ebf2;
  color: #8b97aa;
  &.auto-save-saving {
    color: #1768ff;
  }
  &.auto-save-success {
    color: #168760;
  }
  &.auto-save-error {
    color: #d83232;
  }
}

/* 页面头部（返回 / 标题 / 保存指示器 / 保存 / 下载文件） */
.workbench-top {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  background: #fff;
}
.workbench-top .wb-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 16px;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  // 启用态：白底灰边的返回按钮，hover 时变蓝
  &:not([disabled]) {
    background: #ffffff;
    border: 1px solid #dbe4f2;
    color: #41546d;
    cursor: pointer;
    &:hover {
      border-color: #1768ff;
      color: #1768ff;
    }
  }
  // 禁用态：交给 ant-btn 默认 disabled 样式，仅强制 cursor
  &[disabled] {
    cursor: not-allowed;
  }
}
.workbench-top .wb-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin-right: auto;
}
.workbench-top .wb-name {
  font-size: 18px;
  color: #13213f;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60vw;
}
/* 草稿合同标签：与列表页同款灰色样式，flex-shrink 防止被超长标题挤压 */
.workbench-top .draft-tag {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 8px;
  color: #64748f;
  background: #eef1f6;
  white-space: nowrap;
}
.workbench-top .saved {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8b97aa;
  white-space: nowrap;
}
.workbench-top .save-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c4cbd6;
  display: inline-block;
  &.save-dot-saving {
    background: #1768ff;
  }
  &.save-dot-success {
    background: #1aa97b;
  }
  &.save-dot-error {
    background: #d83232;
  }
}
.workbench-top .wb-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 编辑器顶部条：TaskPanel 收起时才出现，承载“展开AI审查助手”按钮 */
.doc-tabs-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: #fff;
  border-bottom: 1px solid #edf1f7;
}
.expand-assistant-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: none;
  border-radius: 4px;
  background: #fff;
  color: #41546d;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  font-weight: 600;
  .anticon {
    font-size: 14px;
  }
  &:hover {
    border-color: #1768ff;
    color: #1768ff;
    background: #f5f8ff;
  }
}
</style>
